import GraphQLUserRequester from '@cobliteam/api-lib/src/dashboard/user/GraphQLUserRequester';
import type { Session, UserData } from '@cobliteam/common-types';
import useAuthStore from '@cobliteam/hooks/src/useSession/store/useAuthStore';
import useSessionStore from '@cobliteam/hooks/src/useSession/store/useSessionStore';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { QUERY_KEY, QUERY_KEY_USER_DATA } from './queryKeys';

const fetchUserDataFromSession = (session: Session): Promise<UserData> => new GraphQLUserRequester().getUserData({ session });

/**
 * Query canônica de `userData`. Boot otimista: hidratado do `useAuthStore` (persisted) via
 * `initialData` + `initialDataUpdatedAt: 0` para refetch imediato em background.
 *
 * A queryFn lê a sessão do store em runtime (`useAuthStore.getState().session`) em vez de
 * capturar do closure — assim, na primeira render após `useSessionQuery` popular o store,
 * o gate `enabled` fica true e a queryFn já vê a sessão fresca.
 */
const useUserData = (enabled = true): UseQueryResult<UserData, unknown> => {
  const session = useSessionStore((s) => s.session);

  return useQuery<UserData>({
    queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
    queryFn: () => {
      const currentSession = useAuthStore.getState().session;
      if (!currentSession) throw new Error('Session is not defined');
      return fetchUserDataFromSession(currentSession);
    },
    enabled: enabled && !!session,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
    initialData: () => {
      const { userId, userData } = useAuthStore.getState();
      if (userId && userId === session?.user.id && userData) return userData;
      return undefined;
    },
    initialDataUpdatedAt: 0,
  });
};

export { fetchUserDataFromSession };
export default useUserData;
